import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Building2, Wifi, CreditCard, Shield, Users, Bell, Save, Upload, Eye, EyeOff, ArrowLeft, ChevronRight, Plus, Edit2, Trash2, Power, X, CheckCircle, AlertTriangle, Clock, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

const categorias = [
  { id: 'general', label: 'General', icon: Settings, color: 'blue' },
  { id: 'arca', label: 'ARCA (AFIP)', icon: Building2, color: 'purple' },
  { id: 'mikrotik', label: 'MikroTik', icon: Wifi, color: 'orange' },
  { id: 'pagos', label: 'Pagos', icon: CreditCard, color: 'green' },
  { id: 'seguridad', label: 'Seguridad', icon: Shield, color: 'red' },
  { id: 'usuarios', label: 'Usuarios', icon: Users, color: 'indigo' },
  { id: 'notificaciones', label: 'Notificaciones', icon: Bell, color: 'pink' },
];

// Pasarelas de pago disponibles
const pasarelasPago = [
  {
    id: 'mercadopago',
    nombre: 'Mercado Pago',
    descripcion: 'Pagos con tarjeta, billetera y cuotas',
    icono: '💳',
    color: 'blue',
    claves: ['MP_HABILITADO', 'MP_ACCESS_TOKEN', 'MP_PUBLIC_KEY', 'MP_WEBHOOK_SECRET'],
  },
  {
    id: 'siro',
    nombre: 'SIRO - Banco Roela',
    descripcion: 'Pagos con código de barras, QR y billetera virtual',
    icono: '🏛️',
    color: 'indigo',
    claves: ['SIRO_HABILITADO', 'SIRO_CODIGO_COMERCIO', 'SIRO_CLAVE', 'SIRO_URL_PRODUCCION', 'SIRO_URL_SANDBOX', 'SIRO_PRODUCCION'],
  },
];

// ==================== COMPONENTE NOTIFICACIONES ====================

function NotificacionesPanel() {
  const [tabActiva, setTabActiva] = useState('config');
  const queryClient = useQueryClient();

  // Configuraciones de notificaciones
  const { data: configsNotif, isLoading: loadingConfigs } = useQuery({
    queryKey: ['configuraciones', 'notificaciones'],
    queryFn: async () => {
      const response = await api.get('/configuraciones?categoria=notificaciones');
      return response.data;
    },
  });

  // Historial de notificaciones
  const { data: historial, isLoading: loadingHistorial } = useQuery({
    queryKey: ['notificaciones-historial'],
    queryFn: async () => {
      const response = await api.get('/notificaciones/historial');
      return response.data;
    },
    enabled: tabActiva === 'historial',
  });

  const updateMutation = useMutation({
    mutationFn: ({ clave, valor }) => api.post(`/configuraciones/${clave}`, { valor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] });
      toast.success('Configuración guardada');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const getConfigValor = (clave) => {
    const config = configsNotif?.find(c => c.clave === clave);
    return config?.valor || '';
  };

  const toggleConfig = (clave) => {
    const actual = getConfigValor(clave);
    const nuevo = actual === 'true' ? 'false' : 'true';
    updateMutation.mutate({ clave, valor: nuevo });
  };

  const handleChange = (clave, valor) => {
    updateMutation.mutate({ clave, valor });
  };

  const configItems = [
    { clave: 'NOTIF_FACTURA_NUEVA', label: 'Nueva factura generada', desc: 'Notificar al cliente cuando se genera una factura', icon: Mail, color: 'blue' },
    { clave: 'NOTIF_PAGO_RECIBIDO', label: 'Pago recibido', desc: 'Confirmar al cliente al recibir un pago', icon: CheckCircle, color: 'green' },
    { clave: 'NOTIF_VENCIMIENTO', label: 'Recordatorio de vencimiento', desc: 'Avisar X días antes del vencimiento', icon: Clock, color: 'orange' },
    { clave: 'NOTIF_SUSPENSION', label: 'Servicio suspendido', desc: 'Notificar al cliente al suspender el servicio', icon: AlertTriangle, color: 'red' },
    { clave: 'NOTIF_NUEVO_CLIENTE_ADMIN', label: 'Nuevo cliente (Admin)', desc: 'Notificar al admin al crear un cliente', icon: Users, color: 'indigo' },
    { clave: 'NOTIF_PAGO_FALLIDO_ADMIN', label: 'Pago fallido (Admin)', desc: 'Notificar al admin de pagos fallidos', icon: AlertTriangle, color: 'red' },
    { clave: 'NOTIF_REPORTE_DIARIO', label: 'Reporte diario', desc: 'Enviar reporte diario al admin', icon: Mail, color: 'purple' },
    { clave: 'NOTIF_WHATSAPP_HABILITADO', label: 'WhatsApp (Futuro)', desc: 'Habilitar notificaciones por WhatsApp + IA', icon: MessageSquare, color: 'green' },
  ];

  if (loadingConfigs) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-pink-100 rounded-lg">
            <Bell size={24} className="text-pink-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Notificaciones</h2>
            <p className="text-sm text-gray-500">Configura emails automáticos y revisa el historial</p>
          </div>
        </div>
      </div>

      {/* Tabs internas */}
      <div className="flex space-x-2 bg-white p-2 rounded-xl shadow-sm border border-gray-100">
        <button
          onClick={() => setTabActiva('config')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            tabActiva === 'config' ? 'bg-pink-100 text-pink-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          <Settings size={18} />
          <span>Configuración</span>
        </button>
        <button
          onClick={() => setTabActiva('historial')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            tabActiva === 'historial' ? 'bg-pink-100 text-pink-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          <Clock size={18} />
          <span>Historial</span>
        </button>
      </div>

      {tabActiva === 'config' ? (
        <div className="space-y-6">
          {/* Configuraciones principales */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Tipos de Notificación</h3>
            <div className="space-y-4">
              {configItems.map((item) => {
                const Icon = item.icon;
                const activo = getConfigValor(item.clave) === 'true';
                return (
                  <div key={item.clave} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 bg-${item.color}-100 rounded-lg`}>
                        <Icon size={20} className={`text-${item.color}-600`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{item.label}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleConfig(item.clave)}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        activo ? 'bg-pink-600' : 'bg-gray-300'
                      }`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        activo ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Configuraciones avanzadas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Configuración Avanzada</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Días de anticipación para vencimiento
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={getConfigValor('NOTIF_VENCIMIENTO_DIAS') || '3'}
                  onChange={(e) => handleChange('NOTIF_VENCIMIENTO_DIAS', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">Días antes del vencimiento para enviar recordatorio</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hora del reporte diario
                </label>
                <input
                  type="time"
                  value={getConfigValor('NOTIF_REPORTE_HORA') || '08:00'}
                  onChange={(e) => handleChange('NOTIF_REPORTE_HORA', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">Hora en que se envía el reporte diario al admin</p>
              </div>
            </div>
          </div>

          {/* Info de canales futuros */}
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-100 p-6">
            <div className="flex items-center space-x-3 mb-3">
              <MessageSquare size={24} className="text-pink-600" />
              <h3 className="text-lg font-bold text-gray-900">Próximamente: WhatsApp + IA</h3>
            </div>
            <p className="text-gray-600">
              En una futura actualización, las notificaciones también se enviarán por WhatsApp 
              con respuestas automáticas inteligentes. El sistema podrá responder consultas de 
              clientes sobre facturas, pagos y servicios de forma automática.
            </p>
          </div>
        </div>
      ) : (
        /* Tab Historial */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="text-lg font-bold text-gray-900">Historial de Notificaciones</h3>
            <p className="text-sm text-gray-500">Últimas 100 notificaciones enviadas</p>
          </div>
          {loadingHistorial ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
            </div>
          ) : historial?.length === 0 ? (
            <div className="text-center py-12">
              <Bell size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay notificaciones enviadas aún</p>
              <p className="text-sm text-gray-400 mt-1">Las notificaciones aparecerán aquí cuando se envíen</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asunto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {historial?.map((notif) => (
                    <tr key={notif.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          {notif.tipo}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{notif.destinatarioEmail}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{notif.asunto}</td>
                      <td className="px-6 py-4">
                        {notif.enviado ? (
                          <span className="flex items-center text-green-600 text-sm">
                            <CheckCircle size={16} className="mr-1" /> Enviado
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600 text-sm">
                            <AlertTriangle size={16} className="mr-1" /> {notif.error || 'Fallido'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(notif.createdAt).toLocaleString('es-AR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== COMPONENTE CRUD USUARIOS ====================

function UsuariosCRUD() {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(null);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    rol: 'admin',
    password: '',
  });

  const queryClient = useQueryClient();

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      const response = await api.get('/auth/usuarios');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/auth/register', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuario creado exitosamente');
      cerrarModal();
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Error al crear usuario'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/auth/usuarios/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuario actualizado');
      cerrarModal();
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Error al actualizar'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id) => api.patch(`/auth/usuarios/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Estado actualizado');
    },
    onError: () => toast.error('Error al cambiar estado'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/auth/usuarios/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Usuario eliminado');
      setModalEliminar(null);
    },
    onError: () => toast.error('Error al eliminar usuario'),
  });

  const abrirModalCrear = () => {
    setEditando(null);
    setFormData({ nombre: '', email: '', telefono: '', rol: 'admin', password: '' });
    setModalAbierto(true);
  };

  const abrirModalEditar = (usuario) => {
    setEditando(usuario.id);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      telefono: usuario.telefono || '',
      rol: usuario.rol,
      password: '',
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEditando(null);
    setFormData({ nombre: '', email: '', telefono: '', rol: 'admin', password: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editando) {
      const data = { ...formData };
      if (!data.password) delete data.password;
      updateMutation.mutate({ id: editando, data });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Users size={24} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestión de Usuarios</h2>
            <p className="text-sm text-gray-500">Crear, editar y administrar usuarios del sistema</p>
          </div>
        </div>
        <button
          onClick={abrirModalCrear}
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus size={20} />
          <span>Nuevo Usuario</span>
        </button>
      </div>

      {/* Tabla de usuarios */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {usuarios?.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-indigo-600 font-bold text-sm">
                          {usuario.nombre.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{usuario.nombre}</div>
                        <div className="text-sm text-gray-500">{usuario.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      usuario.rol === 'admin' ? 'bg-purple-100 text-purple-700' :
                      usuario.rol === 'operador' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {usuario.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {usuario.telefono || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleMutation.mutate(usuario.id)}
                      className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                        usuario.activo
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {usuario.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => abrirModalEditar(usuario)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => setModalEliminar(usuario)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Crear/Editar */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">
                {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={cerrarModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Nombre completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="usuario@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="(11) 1234-5678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                <select
                  name="rol"
                  value={formData.rol}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="admin">Administrador</option>
                  <option value="operador">Operador</option>
                  <option value="tecnico">Técnico</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editando}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder={editando ? '••••••••' : 'Mínimo 6 caracteres'}
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Guardando...'
                    : editando ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      <ConfirmDeleteModal
        open={!!modalEliminar}
        title="Eliminar Usuario"
        message={
          modalEliminar ? (
            <>¿Estás seguro de eliminar a <strong>{modalEliminar.nombre}</strong>?</>
          ) : ''
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(modalEliminar.id)}
        onCancel={() => setModalEliminar(null)}
      />
    </div>
  );
}

// ==================== COMPONENTE PRINCIPAL ====================

export default function Configuracion() {
  const [categoriaActiva, setCategoriaActiva] = useState('general');
  const [valoresEditados, setValoresEditados] = useState({});
  const [mostrarPrivados, setMostrarPrivados] = useState({});
  const [pasarelaSeleccionada, setPasarelaSeleccionada] = useState(null);
  const queryClient = useQueryClient();

  const { data: configuraciones, isLoading } = useQuery({
    queryKey: ['configuraciones', categoriaActiva],
    queryFn: async () => {
      const response = await api.get(`/configuraciones?categoria=${categoriaActiva}`);
      return response.data;
    },
    enabled: categoriaActiva !== 'usuarios' && categoriaActiva !== 'notificaciones',
  });

  const updateMutation = useMutation({
    mutationFn: ({ clave, valor }) => api.post(`/configuraciones/${clave}`, { valor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] });
      toast.success('Configuracion guardada');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const guardarTodoMutation = useMutation({
    mutationFn: (configs) => api.post('/configuraciones/batch', configs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] });
      toast.success('Todas las configuraciones guardadas');
      setValoresEditados({});
    },
    onError: () => toast.error('Error al guardar configuraciones'),
  });

  const handleChange = (clave, valor) => {
    setValoresEditados(prev => ({ ...prev, [clave]: valor }));
  };

  const handleGuardar = (config) => {
    const valor = valoresEditados[config.clave] !== undefined
      ? valoresEditados[config.clave]
      : config.valor;
    updateMutation.mutate({ clave: config.clave, valor });
  };

  const handleGuardarTodo = () => {
    const configs = Object.entries(valoresEditados).map(([clave, valor]) => ({
      clave,
      valor,
    }));
    if (configs.length > 0) {
      guardarTodoMutation.mutate(configs);
    } else {
      toast('No hay cambios para guardar');
    }
  };

  const toggleMostrarPrivado = (clave) => {
    setMostrarPrivados(prev => ({ ...prev, [clave]: !prev[clave] }));
  };

  const getInputType = (config) => {
    if (config.clave.includes('HABILITADO') || config.clave.includes('PRODUCCION') || config.clave.includes('ABIERTO')) {
      return 'checkbox';
    }
    if (config.esPrivada) return 'password';
    return 'text';
  };

  const categoriaActual = categorias.find(c => c.id === categoriaActiva);
  const IconActivo = categoriaActual?.icon || Settings;

  // Si estamos en la categoría Pagos y no hay pasarela seleccionada, mostrar lista
  const esVistaPasarelas = categoriaActiva === 'pagos' && !pasarelaSeleccionada;

  // Filtrar configs por pasarela seleccionada
  const configsFiltradas = pasarelaSeleccionada && configuraciones
    ? configuraciones.filter(c => pasarelaSeleccionada.claves.includes(c.clave))
    : configuraciones;

  if (isLoading && categoriaActiva !== 'usuarios' && categoriaActiva !== 'notificaciones') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {pasarelaSeleccionada && (
            <button
              onClick={() => {
                setPasarelaSeleccionada(null);
                setValoresEditados({});
              }}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Volver"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h1 className="text-3xl font-bold text-gray-900">
            {pasarelaSeleccionada ? pasarelaSeleccionada.nombre : 'Configuracion'}
          </h1>
        </div>
        {!esVistaPasarelas && categoriaActiva !== 'usuarios' && categoriaActiva !== 'notificaciones' && (
          <button
            onClick={handleGuardarTodo}
            disabled={guardarTodoMutation.isPending || Object.keys(valoresEditados).length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
            <Save size={20} />
            <span>{guardarTodoMutation.isPending ? 'Guardando...' : 'Guardar Todo'}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 bg-white p-2 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        {categorias.map((cat) => {
          const Icon = cat.icon;
          const isActive = categoriaActiva === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => {
                setCategoriaActiva(cat.id);
                setPasarelaSeleccionada(null);
                setValoresEditados({});
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                isActive
                  ? `bg-${cat.color}-100 text-${cat.color}-700 font-medium`
                  : 'text-gray-600 hover:bg-gray-50'
              }`}>
              <Icon size={18} />
              <span>{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Vista de Usuarios */}
      {categoriaActiva === 'usuarios' ? (
        <UsuariosCRUD />
      ) : categoriaActiva === 'notificaciones' ? (
        <NotificacionesPanel />
      ) : esVistaPasarelas ? (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-3 mb-6 pb-4 border-b">
              <div className="p-2 bg-green-100 rounded-lg">
                <IconActivo size={24} className="text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Medios de Pago</h2>
                <p className="text-sm text-gray-500">Selecciona una pasarela para configurar</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pasarelasPago.map((pasarela) => {
                const configHabilitado = configuraciones?.find(c => c.clave === pasarela.claves[0]);
                const estaHabilitada = configHabilitado?.valor === 'true';

                return (
                  <button
                    key={pasarela.id}
                    onClick={() => setPasarelaSeleccionada(pasarela)}
                    className="flex items-center p-6 bg-white border-2 border-gray-100 rounded-xl hover:border-green-300 hover:shadow-md transition-all text-left group"
                  >
                    <div className={`p-4 bg-${pasarela.color}-100 rounded-xl mr-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{pasarela.icono}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-bold text-gray-900">{pasarela.nombre}</h3>
                        {estaHabilitada && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Activo
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{pasarela.descripcion}</p>
                    </div>
                    <ChevronRight size={20} className="text-gray-400 group-hover:text-gray-600" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Vista normal de configuraciones */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-3 mb-6 pb-4 border-b">
            <div className={`p-2 bg-${categoriaActual?.color}-100 rounded-lg`}>
              <IconActivo size={24} className={`text-${categoriaActual?.color}-600`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {pasarelaSeleccionada ? 'Configuracion' : categoriaActual?.label}
              </h2>
              <p className="text-sm text-gray-500">
                {pasarelaSeleccionada
                  ? `Configura los parametros de ${pasarelaSeleccionada.nombre}`
                  : `Configura los parametros de ${categoriaActual?.label.toLowerCase()}`
                }
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {configsFiltradas?.map((config) => {
              const inputType = getInputType(config);
              const valorActual = valoresEditados[config.clave] !== undefined
                ? valoresEditados[config.clave]
                : config.valor;
              const esBooleano = inputType === 'checkbox';

              return (
                <div key={config.clave} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {config.clave.replace(/_/g, ' ')}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">{config.descripcion}</p>
                  </div>
                  <div className="md:col-span-2">
                    {esBooleano ? (
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={valorActual === 'true'}
                          onChange={(e) => handleChange(config.clave, e.target.checked ? 'true' : 'false')}
                          className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">
                          {valorActual === 'true' ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                      </label>
                    ) : config.clave === 'ARCA_CERTIFICADO' || config.clave === 'ARCA_KEY' ? (
                      <div className="space-y-2">
                        <textarea
                          value={valorActual === '••••••••' ? '' : valorActual}
                          onChange={(e) => handleChange(config.clave, e.target.value)}
                          placeholder={`Pega aqui el contenido del archivo ${config.clave === 'ARCA_CERTIFICADO' ? '.crt' : '.key'}...`}
                          rows={4}
                          className="w-full px-3 py-2 border rounded-lg font-mono text-xs focus:ring-2 focus:ring-primary-500"
                        />
                        <p className="text-xs text-gray-500">
                          Copia y pega el contenido completo del archivo {config.clave === 'ARCA_CERTIFICADO' ? 'certificado (.crt)' : 'clave privada (.key)'}
                        </p>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type={mostrarPrivados[config.clave] ? 'text' : inputType}
                          value={valorActual === '••••••••' ? '' : valorActual}
                          onChange={(e) => handleChange(config.clave, e.target.value)}
                          placeholder={config.descripcion}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
                        />
                        {config.esPrivada && (
                          <button
                            type="button"
                            onClick={() => toggleMostrarPrivado(config.clave)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                            {mostrarPrivados[config.clave] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        )}
                      </div>
                    )}
                    {valoresEditados[config.clave] !== undefined && (
                      <button
                        onClick={() => handleGuardar(config)}
                        disabled={updateMutation.isPending}
                        className="mt-2 px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded hover:bg-primary-200">
                        Guardar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
